import React from "react";
import { useState, useEffect } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import Verification from "./components/VerificationScreen";
import ProgressIndicator from "./components/ProgressIndicator";
import { useAuth } from "./hooks/useAuth";
import { useFlow, useFlowSteps } from "./hooks/useFlow";
import UserService from "./lib/UserService";
import ContactInfoScreen from "./components/ContactInfoScreen";
import usePageTracking from "./usePageTracking"; // your tracking hook
import { useErrorTracking } from "./hooks/useAnalytics";
import track from "./lib/analytics/events";
import { useReferrer } from "./hooks/useReferrer";
import ReferrerManager from "./utils/ReferrerManager";
import WelcomeScreen from "./components/WelcomeScreen";
import AlarmSurvey from "./components/nationwide/AlarmSurvey";
import AlarmVerificationForm from "./components/nationwide/AlarmVerificationForm";
import MainLayout from "./components/MainLayout";

// This will be replaced by flow-specific steps from useFlowSteps

// Configuration flag for DIMO integration
const referral = ReferrerManager.getReferrerWithSubdomainFallback();
const App = () => {
  const { step } = useParams();
  const navigate = useNavigate();

  // Get flow configuration
  const { config } = useFlow();
  const STEPS = useFlowSteps();
  const [userData, setUserData] = useState(() => {
    const savedData = sessionStorage.getItem("userData");
    return savedData ? JSON.parse(savedData) : null;
  });
  const {
    user,
    getIdToken,
    loading: authLoading,
    logout,
    firebaseUser,
  } = useAuth(); // Get the authenticated user and loading state
  const [surveyData, setSurveyData] = useState(() => {
    const savedData = sessionStorage.getItem("surveyData");
    return savedData ? JSON.parse(savedData) : {};
  });

  const [isLoading, setIsLoading] = useState(true);
  const [serverUserLoading, setServerUserLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isReturningUser, setIsReturningUser] = useState(false);
  const [initComplete, setInitComplete] = useState(false);
  const [currentStepComponent, setCurrentStepComponent] = useState(null);

  // Analytics hooks
  const { trackError } = useErrorTracking();

  usePageTracking();
  const { isLoading: referrerLoading, displayConfig } = useReferrer();

  // Use an effect to update the component when step changes
  useEffect(() => {
    // Create the component based on the current step and flow
    let component;

    // Handle welcome screen based on flow type
    if (step === STEPS.WELCOME) {
      component = (
        <WelcomeScreen
          isReturningUser={isReturningUser}
          userData={userData}
          onComplete={() => handleStepComplete(STEPS.WELCOME)}
          onFullApp={handleFullAppTransition}
          partnerName={referrerLoading ? undefined : displayConfig.partnerName}
        />
      );
    }
    // Handle other steps
    else {
      switch (step) {
        case STEPS.VERIFICATION:
          component = (
            <Verification
              onComplete={(data) =>
                handleStepComplete(STEPS.VERIFICATION, data)
              }
              onFullApp={handleFullAppTransition}
            />
          );
          break;
        case STEPS.ALARM_VERIFY:
          component = (
            <AlarmVerificationForm />
          );
          break;
          
        case STEPS.CONTACT_INFO:
          component = (
            <ContactInfoScreen
              userData={userData}
              onComplete={(data) =>
                handleStepComplete(STEPS.CONTACT_INFO, data)
              }
              onBack={() => navigateToStep(STEPS.WELCOME)}
            />
          );
          break;
        case STEPS.SURVEY:
          component = (
            <AlarmSurvey
              userData={userData}
              onComplete={(data) =>
                handleStepComplete(STEPS.CONTACT_INFO, data)
              }
              onBack={() => navigateToStep(STEPS.WELCOME)}
            />
          );
          break;
        case STEPS.REWARD:
          // After contact info, send user to the standalone rewards center
          component = <Navigate to="/rewards" replace />;
          break;
        default:
          component = <Navigate to={config.defaultRoute} replace />;
      }
    }
    // Update state with the new component
    setCurrentStepComponent(component);
  }, [step]);

  // Effect for URL params initialization
  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Get URL parameters
        const urlParams = new URLSearchParams(window.location.search);
        const encryptedData = urlParams.get("data");
        const timestamp = urlParams.get("timestamp");
        const partner = urlParams.get("partner");

        if (!encryptedData || !timestamp || !partner) {
          console.warn("Missing some required URL parameters");
          // Don't throw error here, just log a warning
        }

        setInitComplete(true);
      } catch (err) {
        console.error("Error in initialization:", err);
        setError(err.message);
        setInitComplete(true);
      }
    };

    initializeApp();
  }, []);

  useEffect(() => {
    let userCheckInProgress = false;

    const checkUserOnServer = async () => {
      if (!user) {
        return; // No user, no need to check
      }

      if (userCheckInProgress) {
        return; // Prevent duplicate calls
      }

      userCheckInProgress = true;

      try {
        setServerUserLoading(true);
        // Get Firebase token
        const token = await getIdToken();

        // First, just check if the user exists using the /me endpoint
        const serverUserData = await UserService.getUserIfExists(token);
        if (serverUserData) {
          // User exists, store the data
          setUserData(serverUserData);
          sessionStorage.setItem("userData", JSON.stringify(serverUserData));

          // Identify user in analytics
          track.identify(serverUserData.id || user.uid, {
            name: user.displayName,
            email: user.email,
            server_user_id: serverUserData.id,
            is_returning_user: true,
            createdAt: user.metadata?.creationTime,
          });

          if (serverUserData !== null) {
            setIsReturningUser(true);
          }
        } else {
          // User doesn't exist but we're authenticated
          // Don't create yet - we'll do that after explicit authentication
          console.log(
            "User authenticated but not found on server - awaiting verification step"
          );
        }
      } catch (error) {
        console.error("Failed to check server user:", error);
        trackError("server_user_check_error", error, {
          has_user: !!user,
          user_id: user?.uid,
        });
      } finally {
        setServerUserLoading(false);
        userCheckInProgress = false;
      }
    };
    // Only run this if auth loading is complete
    if (!authLoading) {
      checkUserOnServer();
    }

    return () => {
      userCheckInProgress = false;
    };
  }, [user, authLoading, getIdToken]);

  // Combined effect for overall loading state
  useEffect(() => {
    if (!initComplete) {
      // Still initializing from URL params
      return;
    }

    if (authLoading) {
      // Still waiting for Firebase auth
      setIsLoading(true);
      return;
    }

    if (referrerLoading) {
      // Still waiting for referrer/databuyer data
      setIsLoading(true);
      return;
    }

    if (user && serverUserLoading) {
      // Waiting for server user data
      setIsLoading(true);
      return;
    }

    // All loading complete
    setIsLoading(false);
  }, [initComplete, authLoading, referrerLoading, user, serverUserLoading]);

  // Effect for step navigation logic based on user state
  // useEffect(() => {
  //   if (isLoading) return;

  //   // Only skip verification if we have both Firebase auth AND a complete server user
  //   //console.log(userData);
  //   const hasCompleteServerUser = userData && userData.id; // && userData.onboardingCompleted;
  //   //console.log(step + " " + hasCompleteServerUser);

  //   if (step === STEPS.VERIFICATION && hasCompleteServerUser) {
  //     console.log("User already verified with server, skipping verification");
  //     if (isExtendedWarranty()) {
  //       console.log("next step should be " + STEPS.SURVEY);
  //       navigateToStep(STEPS.SURVEY);
  //     } else {
  //       navigateToStep(STEPS.DIMO);
  //     }
  //   }
  // }, [user, userData, step, isLoading]);

  // Effect for returning user logic
  useEffect(() => {
    if (isLoading) return;

    const hasServerUserRecord = userData && userData.id;
    // If user is authenticated and on welcome page, prepare to redirect
    if (hasServerUserRecord && step === STEPS.WELCOME) {
      setIsReturningUser(true);
      // Don't redirect immediately to give welcome screen a chance to display
      // The welcome screen will handle continue action through onComplete
    }
    // For users arriving directly at verification, skip if already authenticated
    if (hasServerUserRecord && step === STEPS.VERIFICATION) {
      // Redirect to post auth survey since user is already verified
      //navigateToStep(STEPS.CONFIRMATION_STATUS);
    }
  }, [user, userData, step, isLoading]);

  // Validate the current step from URL
  useEffect(() => {
    if (isLoading) return;

    const validSteps = Object.values(STEPS);
    if (!validSteps.includes(step)) {
      navigate("/welcome", { replace: true });
    }
  }, [step, navigate, isLoading]);

  const navigateToStep = (nextStep, data = null) => {
    const currentStep = step;

    // Preserve any existing query parameters
    const currentParams = new URLSearchParams(window.location.search);
    const currentQuery = window.location.search;

    // Use flow-aware routing
    const currentUrl = `${config.baseRoute}/${currentStep}${currentQuery}`;
    const targetUrl = `${config.baseRoute}/${nextStep}${currentQuery}`;

    if (currentUrl === targetUrl) {
      console.log("Skipping redundant navigation to", targetUrl);
      return;
    }
    // Preserve important URL parameters
    const preserveParams = [
      "partner",
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_referrer",
      "data",
      "timestamp",
    ];

    const queryParams = new URLSearchParams();
    preserveParams.forEach((param) => {
      if (currentParams.has(param)) {
        queryParams.set(param, currentParams.get(param));
      }
    });

    // Build the query string
    const queryString = queryParams.toString();
    const queryStringFormatted = queryString ? `?${queryString}` : "";

    const replaceSteps = new Set(["welcome", "dimo"]);
    const replace = replaceSteps.has(nextStep);

    // Navigate to the next step using flow-aware routing
    navigate(`${config.baseRoute}/${nextStep}${queryStringFormatted}`, {
      replace: replace,
      // Pass state data if needed
      state: {
        ...(data ? { [nextStep + "Data"]: data } : {}),
      },
    });
  };

  const handleStepComplete = async (step, data) => {
    track.stepCompleted({
      step_name: step,
      has_data: !!data,
      data_keys: data ? Object.keys(data) : [],
      is_returning_user: isReturningUser,
    });

    let nextStep;

    // Use flow-specific logic for step progression
    console.log("go to next step ");
    nextStep = getInsuranceProfileNextStep(step, data);

    if (nextStep) {
      console.log("navigate to next step " + nextStep);
      navigateToStep(nextStep, data);
    }
  };

  // Extended Warranty flow step logic

  // Insurance Profile flow step logic (existing)
  const getInsuranceProfileNextStep = (step, data) => {
    switch (step) {
      case STEPS.WELCOME:
        console.log("isReturningUser", isReturningUser);
        console.log("user", user);
        // Skip verification if user is already logged in
        if (user && firebaseUser) {
          return STEPS.ALARM_VERIFY;
        }
        return STEPS.VERIFICATION;
      case STEPS.VERIFICATION:
        return STEPS.CONFIRMATION_STATUS;
      case STEPS.CONFIRMATION_STATUS:
        return STEPS.CONTACT_INFO;
      case STEPS.SURVEY:
        setSurveyData(data);
        sessionStorage.setItem("surveyData", JSON.stringify(data));
        return STEPS.CONTACT_INFO;
      case STEPS.CONTACT_INFO:
        return STEPS.REWARD;
      case STEPS.SUCCESS:
        return STEPS.SUCCESS;
      default:
        console.error("Unknown step:", step);
        return null;
    }
  };

  const handleFullAppTransition = () => {
    // Generate secure token containing user data and survey responses
    const tokenData = {
      userData,
      surveyData,
      timestamp: Date.now(),
    };

    // In production, this would be properly encrypted
    const token = btoa(JSON.stringify(tokenData));

    // Redirect to full app experience
    window.open(`https://www.ownli.app?token=${token}`, "_blank");
  };

  const LoadingOverlay = () => (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white p-4 rounded-lg shadow-lg flex items-center gap-3">
        <div className="w-5 h-5 border-2 border-t-transparent border-crimsonGlory rounded-full animate-spin"></div>
        <div className="text-gray-800">Loading your information...</div>
      </div>
    </div>
  );

  // Instead of returning just the loading screen, we'll render the app with an overlay
  const showLoadingOverlay = isLoading;

  if (error) {
    return (
      <MainLayout>
        <div className="p-4 w-full flex justify-center">
          <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-lg p-4">
            <h2 className="text-red-800 font-semibold mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <div className="mt-4 space-x-4">
              <button
                onClick={() => window.location.reload()}
                className="text-red-600 hover:text-red-800"
              >
                Retry
              </button>
              <button
                onClick={handleFullAppTransition}
                className="text-red-600 hover:text-red-800"
              >
                Switch to full app
              </button>
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  const rootClassName = step === STEPS.WELCOME ? "bg-white" : "";
  const footerClassName = step === STEPS.WELCOME ? "bg-white" : "bg-gray-50";

  return (
    <MainLayout rootClassName={rootClassName} footerClassName={footerClassName}>
      <div className="flex-1">
        <div className="max-w-md mx-auto">{currentStepComponent}</div>
        {/* Show loading overlay when loading */}
        {showLoadingOverlay && <LoadingOverlay />}
      </div>
    </MainLayout>
  );
};
export default App;
